import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Plus, Search, X } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const QuickButtons = ({ onAddProduct, isAdmin }) => {
  const [buttons, setButtons] = useState(Array(15).fill(null));
  const [assignSlot, setAssignSlot] = useState(null); // slot index being assigned
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);

  const fetchButtons = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/settings/quick-buttons`);
      setButtons(data.buttons || Array(15).fill(null));
    } catch {
      // Silently fail - buttons are optional
    }
  }, []);

  useEffect(() => {
    fetchButtons();
  }, [fetchButtons]);

  const handleQuickAdd = async (btn) => {
    if (!btn) return;
    try {
      // Fetch fresh product data to get current price/stock
      const { data } = await axios.get(`${API}/products/${btn.product_id}`);
      if (data) {
        onAddProduct(data);
      }
    } catch {
      toast.error(`Product not found: ${btn.name}`);
    }
  };

  const searchProducts = async (q) => {
    if (!q || q.length < 2) {
      setSearchResults([]);
      return;
    }
    setSearching(true);
    try {
      const { data } = await axios.get(`${API}/products/search?q=${encodeURIComponent(q)}&limit=15`);
      setSearchResults(data);
    } catch {
      setSearchResults([]);
    } finally {
      setSearching(false);
    }
  };

  const assignProduct = async (product) => {
    if (assignSlot === null) return;
    const btn = {
      product_id: product.id,
      name: product.name,
      plu_code: product.plu_code || "",
      price: product.selling_price_incl_vat || product.retail_price_level1 || 0,
    };
    try {
      await axios.put(`${API}/settings/quick-buttons/${assignSlot}`, { product: btn });
      const updated = [...buttons];
      updated[assignSlot] = btn;
      setButtons(updated);
      setAssignSlot(null);
      setSearchQuery("");
      setSearchResults([]);
      toast.success(`Assigned "${product.name}" to button ${assignSlot + 1}`);
    } catch {
      toast.error("Failed to assign button");
    }
  };

  const clearSlot = async (slot, e) => {
    e.stopPropagation();
    try {
      await axios.put(`${API}/settings/quick-buttons/${slot}`, { product: null });
      const updated = [...buttons];
      updated[slot] = null;
      setButtons(updated);
    } catch {
      toast.error("Failed to clear button");
    }
  };

  const handleButtonClick = (btn, index) => {
    if (btn) {
      handleQuickAdd(btn);
    } else if (isAdmin) {
      setAssignSlot(index);
    }
  };

  const handleContextMenu = (e, index) => {
    if (!isAdmin) return;
    e.preventDefault();
    setAssignSlot(index);
  };

  return (
    <>
      <div
        className="flex flex-wrap gap-1 items-start content-start"
        data-testid="quick-buttons-strip"
      >
        {buttons.map((btn, i) => (
          <button
            key={i}
            onClick={() => handleButtonClick(btn, i)}
            onContextMenu={(e) => handleContextMenu(e, i)}
            className={`relative h-8 px-2 rounded text-[10px] font-semibold leading-tight border transition-all truncate ${
              btn
                ? "bg-slate-700 text-white border-slate-600 hover:bg-slate-600 active:bg-slate-800 min-w-[70px] max-w-[110px]"
                : isAdmin
                ? "bg-slate-100 text-slate-400 border-dashed border-slate-300 hover:bg-slate-200 hover:border-slate-400 w-8"
                : "hidden"
            }`}
            title={btn ? `${btn.name} (Right-click to change)` : "Assign product"}
            data-testid={`quick-btn-${i}`}
          >
            {btn ? (
              <>
                <span className="truncate block pr-3">{btn.name}</span>
                {isAdmin && (
                  <span
                    onClick={(e) => clearSlot(i, e)}
                    className="absolute right-0.5 top-0.5 w-3.5 h-3.5 flex items-center justify-center rounded-full bg-red-500/80 hover:bg-red-600 text-white text-[8px] leading-none opacity-0 hover:opacity-100 group-hover:opacity-100"
                    style={{ opacity: undefined }}
                    onMouseEnter={(e) => (e.target.style.opacity = 1)}
                    onMouseLeave={(e) => (e.target.style.opacity = 0)}
                  >
                    <X className="w-2 h-2" />
                  </span>
                )}
              </>
            ) : (
              <Plus className="h-3 w-3" />
            )}
          </button>
        ))}
      </div>

      {/* Assign Product Dialog */}
      <Dialog open={assignSlot !== null} onOpenChange={(o) => { if (!o) { setAssignSlot(null); setSearchQuery(""); setSearchResults([]); } }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="text-sm">
              Assign Product to Button {assignSlot !== null ? assignSlot + 1 : ""}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="relative">
              <Search className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
              <Input
                placeholder="Search product name or PLU..."
                value={searchQuery}
                onChange={(e) => {
                  setSearchQuery(e.target.value);
                  searchProducts(e.target.value);
                }}
                className="pl-9"
                autoFocus
                data-testid="quick-btn-search"
              />
            </div>
            <div className="max-h-64 overflow-y-auto border rounded-md">
              {searching && (
                <div className="p-3 text-center text-sm text-gray-400">Searching...</div>
              )}
              {!searching && searchResults.length === 0 && searchQuery.length >= 2 && (
                <div className="p-3 text-center text-sm text-gray-400">No products found</div>
              )}
              {searchResults.map((p) => (
                <div
                  key={p.id}
                  onClick={() => assignProduct(p)}
                  className="p-2 hover:bg-blue-50 cursor-pointer border-b last:border-b-0 flex justify-between items-center text-sm"
                  data-testid={`assign-product-${p.id}`}
                >
                  <div>
                    <div className="font-medium">{p.name}</div>
                    <div className="text-xs text-gray-400">PLU: {p.plu_code}</div>
                  </div>
                  <div className="font-mono font-bold text-gray-700">
                    R {(p.selling_price_incl_vat || p.retail_price_level1 || 0).toFixed(2)}
                  </div>
                </div>
              ))}
            </div>
            {buttons[assignSlot] && (
              <Button
                variant="outline"
                size="sm"
                className="w-full text-red-600 hover:text-red-700 hover:bg-red-50"
                onClick={() => clearSlot(assignSlot, { stopPropagation: () => {} }).then(() => { setAssignSlot(null); setSearchQuery(""); setSearchResults([]); })}
                data-testid="clear-quick-btn"
              >
                Clear This Button
              </Button>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};
